/**
 * The conflict tray. SYNC.md Rule 5.
 *
 * The office cancelled the job at 11:00. The technician, offline in a basement in Karrada and
 * with no way to know, completed it at 12:00 and his phone synced at 18:00. The applier
 * refused to guess — it neither forced the completion over the cancellation nor threw away his
 * afternoon — and it parked the photos, the materials and the signature in
 * `orphaned_completions` for a human.
 *
 * This screen is that human's. It has exactly two buttons because there are exactly two
 * answers, and it puts the evidence between the sentence and the buttons because a manager
 * deciding whether to bill an afternoon should be looking at what the man actually did, not at
 * a count of what he did.
 *
 * What this screen must never grow: a "resolve all", a default, a rule, a timer that decides
 * after 48 hours. Every one of those is the machine guessing again, which is the thing the
 * whole sync model refused to do.
 */

import { useState } from "react";
import { Link } from "react-router";
import { JOB_STATE_AR } from "@fanni/shared/job-state";
import { formatIQD } from "@fanni/shared/money";
import { STATUS_TONE, UNIT_AR, fmtDateTime } from "../lib/format.js";
import { useConflict, useConflicts, useResolveConflict } from "../lib/queries.js";
import type { ConflictListRow, OrphanMaterial } from "../lib/types.js";
import { MediaViewingNote, Photo } from "../ui/Photo.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Note,
  PageHeader,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from "../ui/kit.js";

export function Conflicts() {
  const [showResolved, setShowResolved] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error } = useConflicts(showResolved);

  const conflicts = data?.conflicts ?? [];
  const unresolved = conflicts.filter((c) => !c.resolvedAt);

  return (
    <>
      <PageHeader
        title="التعارضات"
        subtitle="إنجازات وصلت من أجهزة الفنيين بعد أن تغيّرت حالة المهمة على الخادم. القرار قرار عمل، لا قرار بيانات — لذلك يقرّره إنسان."
        actions={
          <Button
            variant={showResolved ? "primary" : "default"}
            size="sm"
            onClick={() => setShowResolved((v) => !v)}
          >
            {showResolved ? "إخفاء المحسومة" : "إظهار المحسومة"}
          </Button>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      {isLoading ? (
        <Loading />
      ) : conflicts.length === 0 ? (
        <Card>
          <Empty>لا توجد تعارضات. كل ما أرسله الفنيون انطبق على الخادم كما هو.</Empty>
        </Card>
      ) : (
        <div className="space-y-3">
          {!showResolved && unresolved.length > 0 ? (
            <Note>
              <span className="num">{unresolved.length}</span> تعارض بانتظار قرارك. لا شيء يُحتسب
              ولا يُلغى حتى تقرّر.
            </Note>
          ) : null}
          {conflicts.map((c) => (
            <ConflictCard key={c.id} row={c} onOpen={() => setOpenId(c.id)} open={openId === c.id} />
          ))}
        </div>
      )}
    </>
  );
}

function ConflictCard({
  row,
  open,
  onOpen,
}: {
  row: ConflictListRow;
  open: boolean;
  onOpen: () => void;
}) {
  const resolved = Boolean(row.resolvedAt);

  return (
    <Card
      className={resolved ? "opacity-70" : "border-warn/40"}
      title={
        <span className="flex items-center gap-2">
          {row.jobNumber ? <span className="num">مهمة #{row.jobNumber}</span> : "مهمة"}
          <span className="font-normal text-ink-soft">{row.jobTitle ?? "—"}</span>
          {row.jobStatus ? <Badge tone={STATUS_TONE[row.jobStatus]}>{JOB_STATE_AR[row.jobStatus]}</Badge> : null}
          {resolved ? (
            <Badge tone={row.resolution === "accepted" ? "good" : "neutral"}>
              {row.resolution === "accepted" ? "قُبل الإنجاز" : "بقي الإلغاء"}
            </Badge>
          ) : (
            <Badge tone="warn">بانتظار قرارك</Badge>
          )}
        </span>
      }
      actions={
        <>
          <Link
            to={`/jobs/${row.jobId}`}
            className="text-xs font-medium text-accent hover:underline"
          >
            فتح المهمة
          </Link>
          {!open ? (
            <Button size="sm" onClick={onOpen}>
              عرض الدليل
            </Button>
          ) : null}
        </>
      }
    >
      <ConflictSentence row={row} />
      {open ? <Evidence conflictId={row.id} resolved={resolved} /> : null}
    </Card>
  );
}

/**
 * The sentence the manager reads.
 *
 * Both times are shown and the gap between them is the entire point: he was offline in
 * between and had no way to know.
 *
 * It is composed HERE rather than server-side, and that is the fix for the ISO strings that
 * used to leak into it ("…منذ 2026-07-11T10:53:00.000Z" sitting next to the same instant
 * rendered as 2026/07/11 13:53 in the facts below it). Dates on this screen go through
 * `fmtDateTime` — one formatter, the manager's timezone, no `Intl` and therefore no invisible
 * RTL marks to scramble them inside the LTR-isolated `.num` runs. The API ships the facts; the
 * only place that knows how to render a date for this manager is the thing running in his
 * browser.
 */
function ConflictSentence({ row }: { row: ConflictListRow }) {
  const claimedAt = row.claimed_at;
  const job = row.jobNumber ? `المهمة رقم ${row.jobNumber}` : "المهمة";
  const state = row.job_status_ar ?? "—";

  return (
    <div className="rounded-md border border-line bg-line-soft p-3">
      <p className="text-sm leading-relaxed text-ink">
        {row.technicianName ?? "الفني"} أنهى {job} حسب جهازه في{" "}
        <span className="num">{fmtDateTime(claimedAt)}</span>، لكن حالتها على الخادم «{state}»
        {row.jobCancelledAt ? (
          <>
            {" "}
            منذ <span className="num">{fmtDateTime(row.jobCancelledAt)}</span>
          </>
        ) : null}
        . صوره (<span className="num">{row.mediaIds.length}</span>) ومواده (
        <span className="num">{row.materials.length}</span>) محفوظة.
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-4">
        <Fact label="الفني" value={row.technicianName ?? "—"} />
        <Fact
          label="أنجزها حسب جهازه"
          value={claimedAt ? fmtDateTime(claimedAt) : "—"}
          hint="وقت الجهاز — يُعرض ولا يُرتِّب شيئاً"
        />
        <Fact label="أُلغيت على الخادم" value={fmtDateTime(row.jobCancelledAt)} />
        <Fact label="وصل إلى الخادم" value={fmtDateTime(row.createdAt)} />
      </dl>
      {row.jobCancelReason ? (
        <p className="mt-2 text-xs text-ink-soft">سبب الإلغاء: {row.jobCancelReason}</p>
      ) : null}
    </div>
  );
}

function Fact({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <dt className="text-ink-faint">{label}</dt>
      <dd className="num font-medium text-ink">{value}</dd>
      {hint ? <dd className="text-[11px] text-ink-faint">{hint}</dd> : null}
    </div>
  );
}

function Evidence({ conflictId, resolved }: { conflictId: string; resolved: boolean }) {
  const { data, isLoading, error } = useConflict(conflictId);

  if (isLoading) return <Loading label="جارٍ تحميل الدليل…" />;
  if (error) return <ErrorNote error={error} className="mt-3" />;
  if (!data) return null;

  const materials = data.conflict.materials ?? [];
  const stored = data.media.filter((m) => m.uploadState === "stored").length;
  const signature = data.media.find((m) => m.kind === "signature");
  const photos = data.media.filter((m) => m.kind !== "signature");

  return (
    <div className="mt-4 space-y-4">
      <MediaViewingNote storedCount={stored} />

      <section>
        <h3 className="mb-2 text-xs font-semibold text-ink-soft">
          صوره (<span className="num">{photos.length}</span>)
        </h3>
        {photos.length === 0 ? (
          <p className="text-xs text-ink-faint">لم يرفق صوراً.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {photos.map((m) => (
              <Photo key={m.id} media={m} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold text-ink-soft">
          مواده (<span className="num">{materials.length}</span>)
        </h3>
        {materials.length === 0 ? (
          <p className="text-xs text-ink-faint">لم يسجّل مواد.</p>
        ) : (
          <OrphanMaterials materials={materials} />
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold text-ink-soft">توقيع الزبون</h3>
        {signature ? (
          <Photo media={signature} className="max-w-64" />
        ) : (
          <p className="text-xs text-ink-faint">لا يوجد توقيع محفوظ مع هذا الإنجاز.</p>
        )}
      </section>

      {resolved ? (
        <Note>
          حُسم هذا التعارض. الصور والمواد محفوظة في السجل على أي حال — الحسم يعني «لا تحتسب»، لا
          «امحُ ما فعله الفني».
        </Note>
      ) : (
        <Decision conflictId={conflictId} />
      )}
    </div>
  );
}

/**
 * The orphan's materials come off the `orphaned_completions.payload`, not off `job_materials`
 * — they never landed on the job, which is the whole point. `unit_cost_fils` is the device's
 * estimate (SYNC.md Rule 4: the server recomputes from the price list on a real apply), so
 * this total is labelled as an estimate and is not a number to bill from.
 *
 * The name and unit are joined from the catalogue by GET /conflicts/:id. This table used to
 * print `material_id` — a raw uuid — in the column headed "المادة", which made the one screen
 * whose entire job is "a human decides" undecidable: nobody accepts or discards an afternoon's
 * work on the evidence of `019f6dba-a91e-7f2f…`.
 */
function OrphanMaterials({ materials }: { materials: OrphanMaterial[] }) {
  const total = materials.reduce(
    (a, m) => a + Math.round((Number(m.unit_cost_fils) || 0) * (Number(m.qty) || 0)),
    0,
  );

  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>المادة</Th>
            <Th>الكمية</Th>
            <Th>كلفة الوحدة (تقديري)</Th>
            <Th>ملاحظة</Th>
          </tr>
        </thead>
        <tbody>
          {materials.map((m, i) => (
            <Tr key={String(m.line_id ?? i)}>
              <Td>
                {m.name_ar ? (
                  <span className="font-medium text-ink">{m.name_ar}</span>
                ) : (
                  // The id resolved to nothing in the catalogue. Say that in words — printing
                  // the uuid back would be the original bug wearing a fallback.
                  <span className="text-xs text-ink-faint">مادة غير معروفة في الكتالوغ</span>
                )}
              </Td>
              <Td>
                <span className="num">{String(m.qty ?? "—")}</span>
                {m.unit ? <span className="mr-1 text-xs text-ink-soft">{UNIT_AR[m.unit]}</span> : null}
              </Td>
              <Td>
                <span className="num">{formatIQD(Number(m.unit_cost_fils) || 0)}</span>
              </Td>
              <Td>{m.note ? String(m.note) : "—"}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <p className="mt-2 text-xs text-ink-faint">
        مجموع تقديري: <span className="num font-medium text-ink">{formatIQD(total)}</span> — أسعار
        الجهاز تقديرية، والخادم يعيد حسابها من قائمة الأسعار عند الاحتساب.
      </p>
    </>
  );
}

function Decision({ conflictId }: { conflictId: string }) {
  const [note, setNote] = useState("");
  const resolve = useResolveConflict();

  return (
    <div className="rounded-md border border-line p-3">
      <p className="mb-2 text-sm font-semibold text-ink">ما القرار؟</p>

      {resolve.error ? <ErrorNote error={resolve.error} className="mb-3" /> : null}
      {resolve.data ? <Note className="mb-3">{resolve.data.message_ar}</Note> : null}

      <Textarea
        placeholder="ملاحظة على القرار (اختياري) — تُحفظ مع السجل."
        value={note}
        maxLength={1000}
        onChange={(e) => setNote(e.target.value)}
        className="mb-3"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="good"
          disabled={resolve.isPending || Boolean(resolve.data)}
          onClick={() =>
            resolve.mutate({ id: conflictId, resolution: "accepted", note: note || undefined })
          }
        >
          قبول الإنجاز
        </Button>
        <Button
          variant="danger"
          disabled={resolve.isPending || Boolean(resolve.data)}
          onClick={() =>
            resolve.mutate({ id: conflictId, resolution: "discarded", note: note || undefined })
          }
        >
          الإبقاء على الإلغاء
        </Button>
      </div>

      {/* Both consequences, stated before the click, because both are irreversible in practice. */}
      <ul className="mt-3 space-y-1 text-xs text-ink-faint">
        <li>
          <strong className="text-ink-soft">قبول الإنجاز:</strong> تعود المهمة إلى «مجدولة» على
          لوحة التوزيع، وتكملها بنفسك إلى «منجزة» ثم تُفوترها. لا تقفز إلى «منجزة» تلقائياً.
        </li>
        <li>
          <strong className="text-ink-soft">الإبقاء على الإلغاء:</strong> لا تُحتسب فاتورة. صور
          الفني وموادّه تبقى محفوظة في السجل.
        </li>
      </ul>
    </div>
  );
}
